import { useState, useEffect, useCallback, useRef } from 'react';
import request from 'lib/request';
import type {
  DcaScenarioAreaConfigsResponse,
  DcaScenarioAreaConfigResult,
} from 'types/api-responses';
import type { Polygon } from 'geojson';

export interface AreaGeometry {
  id: number;
  geometry: Polygon | GeoJSON.MultiPolygon | GeoJSON.GeometryCollection | null;
}

/**
 * Normalize area geometry from API (camelCase or snake_case).
 * @param {DcaScenarioAreaConfigResult | Record<string, unknown>} r - API result row
 * @return {unknown} Raw GeoJSON value or null
 */
function getGeoJson(
  r: DcaScenarioAreaConfigResult | Record<string, unknown>
): unknown {
  const row = r as Record<string, unknown>;
  return row.areaGeometryGeoJson ?? row.area_geometry_geo_json ?? null;
}

function parseGeometry(
  raw: unknown
): Polygon | GeoJSON.MultiPolygon | GeoJSON.GeometryCollection | null {
  if (raw === null || raw === undefined) return null;
  const g =
    typeof raw === 'string' ? (JSON.parse(raw) as GeoJSON.Geometry) : raw;
  if (
    g &&
    typeof g === 'object' &&
    'type' in g &&
    (g.type === 'Polygon' ||
      g.type === 'MultiPolygon' ||
      g.type === 'GeometryCollection')
  ) {
    return g as Polygon | GeoJSON.MultiPolygon | GeoJSON.GeometryCollection;
  }
  return null;
}

export function useDcaScenarioAreas(dcaScenarioId: number | null): {
  areas: AreaGeometry[];
  isLoading: boolean;
  error: string | null;
} {
  const [areas, setAreas] = useState<AreaGeometry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchAreas = useCallback(async (id: number) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setError(null);

    const response = await request.get<
      DcaScenarioAreaConfigsResponse & { error?: string }
    >(`/dca_scenarios/${id}/area_configs`, controller);

    if (abortRef.current !== controller) return;

    if ('error' in response && response.error) {
      if (response.error === 'AbortError') return;
      setError(response.error);
      setAreas([]);
      setIsLoading(false);
      return;
    }

    const data = response as DcaScenarioAreaConfigsResponse & {
      results?: DcaScenarioAreaConfigResult[];
    };
    const rawResults =
      data.results ?? (data as { results?: unknown[] }).results ?? [];
    const list = rawResults.map(
      (r: DcaScenarioAreaConfigResult | Record<string, unknown>) => ({
        id: (r as { id: number }).id,
        geometry: parseGeometry(getGeoJson(r)),
      })
    );
    setAreas(list);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (dcaScenarioId === null) {
      setAreas([]);
      setError(null);
      setIsLoading(false);
      if (abortRef.current) abortRef.current.abort();
      return;
    }
    fetchAreas(dcaScenarioId);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [dcaScenarioId, fetchAreas]);

  return { areas, isLoading, error };
}
